import * as React from "react";
import { Menu, Moon, Sun } from "lucide-react";
import { cn } from "../util";
import { Wordmark } from "../marca/Wordmark";
import { Avatar } from "./Avatar";
import { Boton } from "./Boton";
import { Sheet, SheetContenido, SheetDisparador, SheetTitulo } from "./Dialog";

export interface EntradaNav {
  etiqueta: string;
  href: string;
  icono?: React.ReactNode;
  activo?: boolean;
  /** Renderiza la entrada con el Link del router de la app. */
  render?: (props: { className: string; children: React.ReactNode }) => React.ReactNode;
}

export interface AppShellProps {
  /** Nombre del producto, va junto al wordmark: "Vera." + "360". */
  producto: string;
  nav: EntradaNav[];
  persona?: { nombre: string; correo?: string; avatar?: string | null } | null;
  /** Acciones a la derecha de la cabecera (avisos, salir). */
  acciones?: React.ReactNode;
  /** Ligas a las otras apps de iavmx; se muestran al pie de la barra lateral. */
  otrasApps?: { etiqueta: string; href: string }[];
  children: React.ReactNode;
}

function useTema() {
  const [oscuro, setOscuro] = React.useState<boolean>(() => {
    try {
      const guardado = localStorage.getItem("iavmx.tema");
      if (guardado) return guardado === "oscuro";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", oscuro);
    try {
      localStorage.setItem("iavmx.tema", oscuro ? "oscuro" : "claro");
    } catch {
      /* sin almacenamiento, sin drama */
    }
  }, [oscuro]);
  // Ajustes de una app puede cambiar el tema (o volver a "como tu dispositivo"): avisa con este evento.
  React.useEffect(() => {
    const releer = () => {
      try {
        const guardado = localStorage.getItem("iavmx.tema");
        setOscuro(
          guardado
            ? guardado === "oscuro"
            : window.matchMedia("(prefers-color-scheme: dark)").matches,
        );
      } catch {
        /* nada */
      }
    };
    window.addEventListener(EVENTO_TEMA, releer);
    return () => window.removeEventListener(EVENTO_TEMA, releer);
  }, []);
  return { oscuro, alternar: () => setOscuro((v) => !v) };
}

/** Nombre del evento que una app dispara tras escribir localStorage "iavmx.tema" (o borrarlo para seguir al dispositivo). */
export const EVENTO_TEMA = "iavmx:tema";

function Nav({ nav, className }: { nav: EntradaNav[]; className?: string }) {
  return (
    <nav className={cn("flex flex-col gap-0.5", className)} aria-label="Principal">
      {nav.map((e) => {
        const clase = cn(
          "flex items-center gap-2.5 px-3 py-2 font-ui text-sm font-semibold transition-colors border-l-2",
          e.activo
            ? "border-naranja bg-fondo-suave text-tinta"
            : "border-transparent text-tinta-3 hover:bg-fondo-suave hover:text-tinta",
        );
        const contenido = (
          <>
            {e.icono && <span className="[&_svg]:size-4 opacity-80">{e.icono}</span>}
            {e.etiqueta}
          </>
        );
        return (
          <React.Fragment key={e.href}>
            {e.render ? (
              e.render({ className: clase, children: contenido })
            ) : (
              <a href={e.href} className={clase} aria-current={e.activo ? "page" : undefined}>
                {contenido}
              </a>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/** Layout de app: barra lateral fija en escritorio, sheet en móvil, cabecera con persona y tema. */
export function AppShell({ producto, nav, persona, acciones, otrasApps, children }: AppShellProps) {
  const { oscuro, alternar } = useTema();
  const marca = (
    <div className="flex items-baseline gap-2">
      <Wordmark tamano="text-2xl" />
      <span className="font-ui text-sm font-semibold uppercase tracking-wide text-tinta-tenue">
        {producto}
      </span>
    </div>
  );
  const pieApps = otrasApps?.length ? (
    <div className="mt-auto border-t border-linea pt-3">
      <p className="mb-1 px-3 font-ui text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">
        Otras apps
      </p>
      {otrasApps.map((a) => (
        <a
          key={a.href}
          href={a.href}
          className="block px-3 py-1 font-ui text-sm text-azul hover:underline"
        >
          {a.etiqueta}
        </a>
      ))}
    </div>
  ) : null;

  return (
    <div className="flex min-h-screen bg-fondo text-tinta">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-linea bg-fondo p-4 md:flex">
        <div className="mb-6 px-3">{marca}</div>
        <Nav nav={nav} />
        {pieApps}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-linea px-4">
          <Sheet>
            <SheetDisparador asChild>
              <Boton
                variante="fantasma"
                tamano="icono"
                className="md:hidden"
                aria-label="Abrir menú"
              >
                <Menu />
              </Boton>
            </SheetDisparador>
            <SheetContenido lado="izquierda" className="w-64 max-w-[80vw]">
              <SheetTitulo className="sr-only">Menú</SheetTitulo>
              <div className="mb-4">{marca}</div>
              <Nav nav={nav} />
              {pieApps}
            </SheetContenido>
          </Sheet>
          <div className="md:hidden">{marca}</div>
          <div className="ml-auto flex items-center gap-2">
            {acciones}
            <Boton
              variante="fantasma"
              tamano="icono"
              onClick={alternar}
              aria-label={oscuro ? "Modo claro" : "Modo oscuro"}
            >
              {oscuro ? <Sun /> : <Moon />}
            </Boton>
            {persona && (
              <div className="flex items-center gap-2 pl-2">
                <Avatar nombre={persona.nombre} src={persona.avatar} className="size-8" />
                <div className="hidden leading-tight sm:block">
                  <p className="font-ui text-sm font-semibold">{persona.nombre}</p>
                  {persona.correo && (
                    <p className="font-ui text-xs text-tinta-tenue">{persona.correo}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}

/** Encabezado de página: título Bitter con punto naranja y acción a la derecha. */
export function TituloPagina({
  children,
  accion,
  descripcion,
}: {
  children: React.ReactNode;
  accion?: React.ReactNode;
  descripcion?: string;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-linea pb-4">
      <div>
        <h1 className="font-titulo text-3xl font-black leading-none">{children}</h1>
        {descripcion && <p className="mt-2 font-ui text-sm text-tinta-tenue">{descripcion}</p>}
      </div>
      {accion}
    </div>
  );
}
